import { useMutation } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { TestResponse } from '@/Domain/api/Action/dto/Action.dto';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { HttpTestResponseForm } from '@/Domain/components/Action/HttpRequestActionSheet/TestResponse/TestResponse';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import {
  ValidationCheckError,
  getValidationCheckMessage,
} from '@/Domain/helper/actionValidation.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

export const useTestRequest = () => {
  const { t } = useTranslation('actions_page');
  const [jsonResponse, setJsonResponse] = useState<TestResponse | null>(null);
  const { capture } = usePostHogAnalytics();

  const mutation = useMutation({
    mutationKey: [QueryKey.actions.httpRequest.test],
    mutationFn: (data: HttpTestResponseForm) => ActionsApi.testRequest(data),
    onError: (error: AxiosError<ErrorResponse>) => {
      if (error instanceof AxiosError) {
        if (error.status === HttpStatusCode.BadRequest) {
          const firstError = error.response?.data?.errors?.root?.[0];

          const validationChecks = firstError?.options?.validation_checks;
          if (validationChecks) {
            const message = getValidationCheckMessage(validationChecks as ValidationCheckError, t);
            if (message) {
              toast.error(message);
              return;
            }
          }

          const serverMessage = firstError?.message;
          if (serverMessage) {
            toast.error(serverMessage);
            return;
          }
        }

        toast.error(t('actions_page:sheet.form.validation.test_failed'));
      }
    },
    onSuccess: (data) => {
      capture(PostHogEventEnum.ActionTested, {
        status_code: data.http_code,
        success: data.http_code !== null && data.http_code >= 200 && data.http_code < 300,
      });
      setJsonResponse(data);
    },
  });

  const handleTestRequest = (data: HttpTestResponseForm) => mutation.mutate(data);

  return { handleTestRequest, ...mutation, jsonResponse };
};
